// [VV14-TEARDOWN] cart-basket-recommender A/B test (DEV-10023).
// vv14: in the store cart drawer, the `test` arm sources the add-ons block
// from the Luigi's Box `basket` recommender (keyed on every item in the cart)
// instead of the channel's manually curated addon list. Block layout, heading,
// promo prices, colour x size selector, inline add-to-cart and item count are
// identical in both arms: only where the four products come from changes.
//
// Client-safe: this module must NOT import `next/server` (unlike
// `../middleware/cartRecommender.ts`), because the cart drawer pulls it into
// the client bundle.
//
// ─── HOW THIS TEST IS ASSIGNED ──────────────────────────────────────────────
// Standard house pattern: deterministic FNV-1a bucketing of IP + User-Agent
// into 50 / 50 control / test.
//
// No path scope, like vv9 and unlike vv5 / vv11 / vv13. The cart drawer mounts
// in the store group layout, so it is present on every store page and there is
// no narrower path that means "this visitor can see the block". Assignment is
// therefore unscoped and enrolment is what carries the scope: exposure fires
// only where the two arms can actually differ (see the gate below).
//
// ─── TEARDOWN CHECKLIST (grep `VV14-TEARDOWN` across the repo) ──────────────
// DELETE these files (entirely test-only):
//   packages/tracking/src/experiments/cartRecommender.ts        (this file)
//   packages/tracking/src/middleware/cartRecommender.ts
//   packages/tracking/src/middleware/getVv14.ts
//   packages/tracking/src/__tests__/cartRecommenderMiddleware.test.ts
//   packages/tracking/src/__tests__/cartRecommenderExperiment.test.ts
//   packages/offer/src/modules/store/Cart/BasketRecommendations.tsx
//   packages/offer/src/modules/store/Cart/Vv14ExposureBeacon.tsx
//   docs/experimentation-ab-test-cart-recommender.md
//
// REVERT the marked `[VV14-TEARDOWN]` blocks in:
//   packages/tracking/src/middleware/index.ts      (option, resolver, header, cookie)
//   packages/tracking/src/middleware/url/const.ts  (drop the "vv14" entry outright, per #4026 convention)
//   packages/tracking/src/trackActions.tsx         (split_test membership)
//   apps/store/src/proxy.ts                        (drop `vv14: true`)
//   packages/offer/src/modules/store/Cart/CartSlideOverWithRecommendations.tsx (server read + prop)
//   packages/offer/src/modules/store/Cart/CartSlideOver.tsx       (prop pass-through)
//   packages/offer/src/modules/store/Cart/CartContents.tsx        (prop + arm switch)
//   packages/offer/src/modules/store/Cart/Recommendations.tsx     (beacon source props)
//   packages/offer/src/modules/common/StaticPages/CookiePolicy/en.tsx (vv14 cookie row)
//   packages/offer/src/modules/common/StaticPages/CookiePolicy/de.tsx (vv14 cookie row, DE)
//   apps/store/src/tests/policy-pages.spec.ts      (vv14 cookie-policy assertion)
//   packages/devtools/src/panel/components/ABTestingTab.tsx (vv14 EXPERIMENTS entry)
//
// KEEP either way: `packages/search/src/client/recommendFetcher.ts`, the
// `/api/recommend` route and `packages/search/src/api/recommend.ts`. They
// predate this test and are not test-only.
//
// EXTERNAL: archive the `cart-basket-recommender` PostHog feature flag. If the
// test arm wins, the recommender becomes the block's only data source and the
// curated `channel.addons` path is removed, NOT left behind this cookie gate.
// ───────────────────────────────────────────────────────────────────────────

/** Cookie / query-override slot name for the vv14 experiment. */
export const VV14_COOKIE = "vv14"

/**
 * PostHog feature-flag key for this experiment (single source of truth).
 * The `control` / `test` cookie buckets map 1:1 to this flag's variant keys.
 */
export const VV14_FEATURE_FLAG = "cart-basket-recommender"

/**
 * Valid variant values for the vv14 experiment.
 *   - control -> today's curated `channel.addons` list
 *   - test    -> Luigi's Box `basket` recommendations for the current cart
 */
export const VV14_VARIANTS = ["control", "test"] as const

export type Vv14Variant = (typeof VV14_VARIANTS)[number]

/** Type guard for anything read back from the cookie / query override. */
export const isVv14Variant = (value: unknown): value is Vv14Variant =>
  VV14_VARIANTS.includes(value as Vv14Variant)

/**
 * Whether this render of the add-ons block belongs to the recommender arm.
 *
 * A missing / invalid / control value keeps today's behaviour exactly, which
 * is also what every surface that is passed no assignment gets: the `/cart`
 * page and the empty checkout render the same block and stay on the curated
 * list in both arms.
 *
 * An empty cart is deliberately NOT part of this rule. The `basket`
 * recommender takes the cart contents as its input, so with an empty cart it
 * has nothing to key on and the test arm shows the curated list instead. That
 * is a data condition the component resolves (its SWR key is null), not a
 * policy, and `isCartRecommenderExposed` below keeps those sessions out of the
 * denominator either way.
 *
 * `hasCuratedItems` IS part of the rule, and it is what keeps the split
 * honest. Control renders nothing when the channel has no addons, or when the
 * channel fetch failed and the drawer fell back to an empty list. Letting the
 * test arm fill that block anyway would enrol only one arm in those sessions:
 * a sample-ratio mismatch, and a comparison against nothing. Where control
 * has no block, the test arm has no block either.
 */
export const isBasketRecommenderActive = ({
  vv14,
  surface,
  hasCuratedItems,
}: {
  vv14?: string | null
  surface: string
  hasCuratedItems: boolean
}): boolean => vv14 === "test" && surface === "cart-drawer" && hasCuratedItems

/**
 * Whether this render of the add-ons block should enrol the visitor.
 *
 * True for BOTH arms, because the exposed population is what the take rate is
 * divided by. Two conditions narrow it to the sessions where the arms can
 * actually differ:
 *
 *  - the cart drawer only. The same block also renders on `/cart` and on the
 *    empty checkout, and those surfaces stay on the curated list in both arms.
 *  - a non-empty cart, for the reason in `isBasketRecommenderActive`. Enrolling
 *    empty-cart sessions would pad the denominator with sessions in which both
 *    arms are byte-identical and dilute whatever effect exists.
 *
 * A third condition is enforced by where the beacon mounts rather than here:
 * it sits INSIDE the block, past the block's own empty check, so a session
 * that was shown no add-ons cannot be enrolled by either arm. Keep it there.
 * Passing that fact in as an argument would be passing a constant, since the
 * only call site is already inside a block that rendered.
 */
export const isCartRecommenderExposed = ({
  surface,
  hasCartItems,
}: {
  surface: string
  hasCartItems: boolean
}): boolean => surface === "cart-drawer" && hasCartItems
